import React, { FC } from 'react';
import { RestrictTo, TextWithEllipsis, useConfig, useDate } from '@bechtel/client-common';
import { Text, Box, Icon, IconSize, Checkbox } from '@bechtel/gds';
import { CommodityStatusCode } from '@bechtel/types';
import { t } from '@lingui/macro';
import { BoxStyled, EllipseStyled, PriorityWeldIconStyled } from './styled';
import { WeldDetailActions, WeldAvatarGroup } from '@bechtel/client-weld-common';
import { WeldItemProps } from './types';
import { css } from 'styled-components';
import { parseISO } from 'date-fns';
import { ReactComponent as WeldingRequired } from '@bechtel/client-assets/dist/images/welding-required.svg';

export const WeldItem: FC<WeldItemProps> = ({
  weld,
  showSelectOption,
  onSelectWeldItem,
  isChecked,
  allowItemSelection,
  personaType,
  createRodRequest,
}) => {
  const { formatRelativeInWords } = useDate();
  const { dateFormat } = useConfig();
  return (
    <Box px={{ sm: '400' }} data-testid="weld-item-container">
      <BoxStyled>
        <Box mr={{ sm: '300' }} css={{ minWidth: '25%', maxWidth: '35%' }}>
          {showSelectOption && (
            <Checkbox
              onChange={() => {
                onSelectWeldItem(weld.id);
              }}
              css={`
                ${allowItemSelection ? null : { opacity: 0.4, pointerEvents: 'none' }}
                ${({ theme }) =>
                  css`
                    margin-top: ${theme.spacings[200]};
                  `}
              `}
              checked={isChecked}
            />
          )}
          <Box flexDirection={{ sm: 'column' }} ml={{ sm: '300' }} css={{ maxWidth: '100%' }}>
            <Box alignItems={{ sm: 'center' }}>
              <TextWithEllipsis
                data-testid="weld-tag"
                family="graphik"
                size="200"
                color={weld.status?.status == CommodityStatusCode.BLOCKED ? 'red.700' : 'grey.1000'}
                weight="600"
                transform="uppercase"
              >
                {weld.tag}
              </TextWithEllipsis>
            </Box>

            <Box mb={{ sm: '200' }} mt={{ sm: '300' }} alignItems={{ sm: 'center' }}>
              {weld?.status?.statusAt && (
                <Text size="200" color="grey.700" family="graphik" style={{ opacity: '0.7' }}>
                  {formatRelativeInWords(
                    parseISO(weld?.status?.statusAt),
                    new Date(),
                    RestrictTo.Day,
                    dateFormat,
                    ``,
                    false
                  )}
                </Text>
              )}
              <Text size="400" color="grey.800" family="graphik" style={{ margin: '0 5px' }}>
                /
              </Text>
              {weld.pipeMaterial && (
                <Box data-testid="weld-pipe-material" mr={{ sm: '300' }}>
                  <Text size="200" color="grey.700">
                    {weld.pipeMaterial}
                  </Text>
                </Box>
              )}
              {weld.pipeDiameter && (
                <>
                  <EllipseStyled mr={{ sm: '300' }} />
                  <Text data-testid="weld-pipe-diameter" size="300" color="grey.700">
                    {`${weld.pipeDiameter}${t`" Dia`}`}
                    &nbsp;
                  </Text>
                </>
              )}
              {weld?.isPriority && (
                <>
                  <EllipseStyled />
                  <PriorityWeldIconStyled>
                    <Icon icon="flame" size={IconSize.XSMALL} fillColor="white" />
                  </PriorityWeldIconStyled>
                </>
              )}
              {weld?.fitupRequest?.isWeldingRequired && (
                <Box ml={{ sm: '400' }}>
                  <WeldingRequired style={{ width: '24px', height: '16px' }} />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Box
          ml={{ sm: '400' }}
          css={`
            ${({ theme }) => `height: ${theme.spacings[800]};`}
          `}
        >
          <WeldAvatarGroup
            weldAssignees={weld.commodityAssignees}
            personaType={personaType}
            currentMilestoneType={weld.currentMilestoneType}
          />
        </Box>
        <Box alignItems={{ sm: 'flex-end' }}>
          <WeldDetailActions weld={weld} personaType={personaType} createRodRequest={() => createRodRequest(weld)} />
        </Box>
      </BoxStyled>
    </Box>
  );
};
